import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Edit, DollarSign, FileText, TrendingUp, Truck, Package, List } from 'lucide-react';

const apiUrl = process.env.REACT_APP_API_URL;

export default function HomePage() {
  const navigate = useNavigate();
  const [latestInvoices, setLatestInvoices] = useState([]);
  const [latestPayments, setLatestPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLoading(false);
          return;
        }

        // Fetch latest invoices
        const invoicesResponse = await fetch(`${apiUrl}/invoices`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (invoicesResponse.ok) {
          const invoicesData = await invoicesResponse.json();
          // Get latest 5 invoices from the data array
          if (invoicesData.data && Array.isArray(invoicesData.data)) {
            setLatestInvoices(invoicesData.data.slice(0, 5));
          }
        }

        // Fetch latest payments from all invoices
        const paymentsResponse = await fetch(`${apiUrl}/payments/latest`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (paymentsResponse.ok) {
          const paymentsData = await paymentsResponse.json();
          if (Array.isArray(paymentsData)) {
            setLatestPayments(paymentsData.slice(0, 5));
          }
        }

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg text-gray-600">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
      {/* Dashboard Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Latest Invoices */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="text-blue-600" size={24} />
                <h3 className="text-lg font-semibold text-gray-900">Latest Invoices</h3>
              </div>
              <button
                onClick={() => navigate('/invoices/list')}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                View All
              </button>
            </div>
          </div>
          <div className="p-4">
            {latestInvoices.length > 0 ? (
              <div className="space-y-3">
                {latestInvoices.map((invoice) => (
                  <div key={invoice.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900">
                          Invoice #{invoice.invoice_number}
                        </span>
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          invoice.status === 'complete'
                            ? 'bg-green-100 text-green-800'
                            : invoice.status === 'draft'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {invoice.status}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {invoice.partner_name || invoice.customer_name || 'No Partner'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(invoice.date || invoice.invoice_date).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-gray-900">
                        {parseFloat(invoice.total || 0).toFixed(2)} ден
                      </div>
                      <div className="flex justify-end mt-2">
                        <button
                          onClick={() => navigate(`/invoices/view/${invoice.id}`)}
                          className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 transition-colors"
                          title="View Invoice"
                        >
                          <ExternalLink size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText size={48} className="mx-auto mb-2 text-gray-300" />
                <div className="text-sm">No invoices found</div>
                <div className="text-xs text-gray-400 mt-1">
                  Create your first invoice to get started
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Latest Payments */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="text-green-600" size={24} />
                <h3 className="text-lg font-semibold text-gray-900">Latest Payments</h3>
              </div>
              <button
                onClick={() => navigate('/payments/list')}
                className="text-green-600 hover:text-green-800 text-sm font-medium"
              >
                View All
              </button>
            </div>
          </div>
          <div className="p-4">
            {latestPayments.length > 0 ? (
              <div className="space-y-3">
                {latestPayments.map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900">
                          Invoice #{payment.invoice_number}
                        </span>
                        <span className="text-xs text-gray-500 capitalize">
                          {payment.payment_method?.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {payment.customer_name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(payment.payment_date).toLocaleDateString()}
                      </div>
                      {payment.note && (
                        <div className="text-xs text-gray-400 mt-1">
                          {payment.note}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-green-600">
                        +{parseFloat(payment.amount).toFixed(2)} ден
                      </div>
                      <div className="flex justify-end mt-2">
                        <button
                          onClick={() => navigate(`/invoices/view/${payment.invoice_id}`)}
                          className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 transition-colors"
                          title="View Invoice"
                        >
                          <ExternalLink size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <DollarSign size={48} className="mx-auto mb-2 text-gray-300" />
                <div className="text-sm">No payments found</div>
                <div className="text-xs text-gray-400 mt-1">
                  Payments will appear here as they are recorded
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <TrendingUp className="text-purple-600" size={24} />
          Quick Actions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <button
            onClick={() => navigate('/invoices/create')}
            className="flex items-center gap-3 p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-left"
          >
            <FileText className="text-blue-600" size={20} />
            <div>
              <div className="font-medium text-gray-900">Create Invoice</div>
              <div className="text-xs text-gray-600">Generate a new invoice</div>
            </div>
          </button>
          <button
            onClick={() => navigate('/invoices/create')}
            className="flex items-center gap-3 p-3 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors text-left"
          >
            <Truck className="text-orange-600" size={20} />
            <div>
              <div className="font-medium text-gray-900">Create Delivery Note</div>
              <div className="text-xs text-gray-600">Generate delivery note</div>
            </div>
          </button>
          <button
            onClick={() => navigate('/products/list')}
            className="flex items-center gap-3 p-3 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors text-left"
          >
            <Package className="text-purple-600" size={20} />
            <div>
              <div className="font-medium text-gray-900">Manage Products</div>
              <div className="text-xs text-gray-600">Add or edit products</div>
            </div>
          </button>
          <button
            onClick={() => navigate('/invoices/list')}
            className="flex items-center gap-3 p-3 bg-green-50 hover:bg-green-100 rounded-lg transition-colors text-left"
          >
            <List className="text-green-600" size={20} />
            <div>
              <div className="font-medium text-gray-900">View Invoices</div>
              <div className="text-xs text-gray-600">Browse all invoices</div>
            </div>
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}
